import type { GetCustomWorklistQuery } from 'generated/graphql';

import { SEARCH_DEBOUNCE_MS, SORTABLE_WORKLIST_COLUMN_MAP } from 'config/constants';
import { SortColumn, SortOrder } from 'generated/graphql';
import { useDebouncedState } from 'hooks/useDebouncedState';
import {
  DEFAULT_EMPTY_WORKLIST_SURFACE_FILTERS,
  DEFAULT_SORT_COLUMNS,
  DEFAULT_SORT_ORDERS,
} from './constants';

import assoc from 'ramda/es/assoc';
import equals from 'ramda/es/equals';
import {
  useContext,
  createContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';
import { useWorklistURLParams } from '../Worklist/useWorklistURLParams';
import {
  FilterDefinitionType,
  FilterStateBySurface,
  FilterStateType,
  WorklistFiltersType,
  WorklistSurfaceSearchValues,
  FilterVariant,
  WorklistFilters,
} from './types';
import { useGenerateFilterConfig } from './hooks/useGenerateFilterConfig';
import { logger } from 'modules/logger';
import { WorklistSurface, type WorklistSurfaceType } from '../Worklist/types';
import {
  SaveSortingPreferencesArgs,
  useSavedSortingPreferences,
} from '../Worklist/hooks/useSavedSortingPreferences';
import {
  getWorklistURLParams,
  parseFiltersFromURLParams,
  validateSortColumns,
  validateSortOrders,
} from './utils';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useIsFilterPersistenceDisabled } from '../Worklist/hooks/useIsFilterPersistenceDisabled';
import { useLazyQuery } from '@apollo/client';
import { GET_CUSTOM_WORKLIST } from 'modules/Apollo/queries';
import { useRecoilState } from 'recoil';
import { activeCustomWorklistFiltersState, activeCustomWorklistIDsState } from '../Worklist/state';
import {
  formatCustomWorklistFilters,
  getCustomWorklistNames,
} from '../Worklist/WorklistPageV2/utils';
import { useSaveLastViewedCustomWorklists } from '../Worklist/hooks/useLastViewedCustomWorklists';
import useMount from 'react-use/esm/useMount';
import { CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY } from '../Worklist/WorklistPageV2/constants';
import { hasSameItems, getWorklistKey } from '../Worklist/utils/worklistUtils';
import { isAnyProvidedCannedWorklist } from '../Worklist/utils/isProvidedCannedWorklist';
import { useCustomWorklists } from '../Worklist/hooks/useCustomWorklists';
import {
  useCustomWorklistSortingPreferences,
  useCustomWorklistPreferences,
  useCustomWorklistFilterPreferences,
} from '../Worklist/hooks/useCustomWorklistPreferences';
import { useCustomWorklistPreferenceUpdates } from '../Worklist/hooks/useCustomWorklistPreferenceUpdates';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { worklist } from 'modules/analytics/constants';
import { buildFilterDetails } from '../analytics/utils';
import { WorklistMode, WorklistTypeDetail, WorklistTypeDetailType } from '../analytics/state';
import { trackWorklistAction } from '../analytics/useWorklistAnalytics';
import { telemetryContextFor } from 'modules/Apollo/types';

export type WorklistFiltersContextProps = Readonly<{
  defaultSurface?: WorklistSurfaceType;
  children: React.ReactNode;
  overrideURLSearchParams?: Nullable<URLSearchParams>;
  overrideSurface?: Nullable<WorklistSurfaceType>;
  defaultCustomWorklistIDs?: string[];
  skipWorklistQueries?: boolean;
}>;

export type WorklistFiltersContextBag = {
  surface: WorklistSurfaceType;
  updateSurface: (arg1: WorklistSurfaceType) => void;
  availableFilters: FilterDefinitionType;
  filters: FilterStateType;
  lockedFilters: FilterStateType;
  updateFilters: (arg1: FilterStateType) => void;
  replaceFilters: (arg1: FilterStateType) => void;
  resetAllFilters: () => void;
  resetFilter: (arg1: WorklistFiltersType) => void;
  sortColumns: Array<SortColumn>;
  sortOrders: Array<SortOrder>;
  onSortChange: (columnId: string[], order: Array<SortOrder>) => void;
  setSearchForSurface: (value: string) => void;
  setSearchForCustomWorklist: (customWorklistID: string, value: string) => void;
  debouncedSearchForSurface: string;
  searchForSurface: string;
  getAppliedFilters: () => Array<string>;
  filtersBySurface: FilterStateBySurface;
  debouncedSearches: WorklistSurfaceSearchValues;
  surfacesToDefaultFilters: {
    [key: string]: FilterDefinitionType;
  };
  surfacesToLockedFilters: {
    [key: string]: FilterStateType;
  };
  updateCombinedCustomWorklists: (
    activeWorklistIds: string[]
  ) => Promise<Nullable<FilterStateType>>;
  updateSurfaceAndCustomWorklist: (customWorklistID: string) => Promise<void>;
  updateSurfaceAndSearch: (newSurface: WorklistSurfaceType, newSearch: string) => void;
  areCustomWorklistFiltersLoading: boolean;
  isStillProcessingCustomWorklistFiltersRef: MutableRefObject<boolean>;
  requestedCustomWorklistIDsRef: MutableRefObject<string | null>;
};

const WorklistFiltersContext = createContext<Nullable<WorklistFiltersContextBag>>(undefined);

export const WorklistFiltersContextProvider = ({
  children,
  defaultSurface = WorklistSurface.CUSTOM_WORKLIST,
  defaultCustomWorklistIDs = [],
  overrideURLSearchParams,
  overrideSurface,
  skipWorklistQueries = false,
}: WorklistFiltersContextProps): React.ReactElement => {
  const [isRefactorEnabled] = useFeatureFlagEnabled(FF.WORKLIST_CONTEXT_REFACTOR);
  const [isQcStatusColumnEnabled] = useFeatureFlagEnabled(FF.QC_STATUS_COLUMN);
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT, skip: skipWorklistQueries });

  const {
    surface: urlSurface,
    sortColumn: urlSortColumns,
    sortOrder: urlSortOrders,
    filters: urlFilters,
  } = useWorklistURLParams(overrideURLSearchParams);

  const urlFiltersOnLoad = useRef(urlFilters);
  const currentCustomWorklistRequest = useRef<string | null>(null);
  const hasMountedRef = useRef(false);

  const isStillProcessingCustomWorklistFiltersRef = useRef(false);
  const requestedCustomWorklistIDsRef = useRef<string | null>(null);

  const [activeCustomWorklistIDs, setActiveCustomWorklistIDs] = useRecoilState(
    activeCustomWorklistIDsState
  );
  const [activeCustomWorklistFilters, setActiveCustomWorklistFilters] = useRecoilState(
    activeCustomWorklistFiltersState
  );
  const { allWorklists, isPublicOrCombinedWorklist, privateWorklists } = useCustomWorklists({
    queryOptions: {
      context: APOLLO_QUERY_CONTEXT,
      skip: skipWorklistQueries,
    },
  });
  const activeCustomWorklistNames = useMemo(() => {
    if (activeCustomWorklistIDs == null || activeCustomWorklistIDs.length === 0) {
      return [];
    }
    return getCustomWorklistNames(activeCustomWorklistIDs, allWorklists).filter(
      (name): name is string => name != null
    );
  }, [activeCustomWorklistIDs, allWorklists]);

  const customWorklistPreferences = useCustomWorklistPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined: isPublicOrCombinedWorklist(activeCustomWorklistIDs),
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });
  const customWorklistSortingPreferences = useCustomWorklistSortingPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined: isPublicOrCombinedWorklist(activeCustomWorklistIDs),
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });
  const customWorklistFilterPreferences = useCustomWorklistFilterPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined: isPublicOrCombinedWorklist(activeCustomWorklistIDs),
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const isFilterPersistenceDisabled = useIsFilterPersistenceDisabled({
    context: APOLLO_QUERY_CONTEXT,
    skip: skipWorklistQueries,
  });

  const { updateCustomWorklistSortingSettings, updateCustomWorklistFilterSettings } =
    useCustomWorklistPreferenceUpdates({
      activeCustomWorklistIDs,
      privateWorklists,
      worklistSettings: data?.me?.worklistSettings,
      customWorklistSettings: customWorklistPreferences,
      userSmid: data?.me?.id,
      skipQueries: skipWorklistQueries,
      context: APOLLO_QUERY_CONTEXT,
    });

  const initialSurface: WorklistSurfaceType = (() => {
    if (defaultSurface === WorklistSurface.STUDIES_DIALOG) {
      return defaultSurface;
    }

    if (defaultCustomWorklistIDs.length > 0) {
      return WorklistSurface.CUSTOM_WORKLIST;
    }

    if (urlSurface != null) {
      return urlSurface;
    }

    return WorklistSurface.PLACEHOLDER;
  })();

  const { surfaceToFilterConfig } = useGenerateFilterConfig({
    defaultSurface,
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const [surface, setSurface] = useState<WorklistSurfaceType>(initialSurface);

  const shouldUseCustomWorklistPreferences = useMemo(() => {
    return surface === WorklistSurface.CUSTOM_WORKLIST;
  }, [surface]);

  // Initialize sorting state - use custom worklist preferences if available, otherwise use URL params
  const initialSortColumns = useMemo(() => {
    if (shouldUseCustomWorklistPreferences && customWorklistSortingPreferences?.sortColumns) {
      return customWorklistSortingPreferences.sortColumns;
    }
    return urlSortColumns;
  }, [
    shouldUseCustomWorklistPreferences,
    customWorklistSortingPreferences?.sortColumns,
    urlSortColumns,
  ]);

  const initialSortOrders = useMemo(() => {
    if (shouldUseCustomWorklistPreferences && customWorklistSortingPreferences?.sortOrders) {
      return customWorklistSortingPreferences.sortOrders;
    }
    return urlSortOrders;
  }, [
    shouldUseCustomWorklistPreferences,
    customWorklistSortingPreferences?.sortOrders,
    urlSortOrders,
  ]);

  const [sortColumns, setSortColumns] = useState<Array<SortColumn>>(initialSortColumns);
  const [sortOrders, setSortOrders] = useState<Array<SortOrder>>(initialSortOrders);

  const { saveSortingPreferences, savedSortingPreferences } = useSavedSortingPreferences({
    context: APOLLO_QUERY_CONTEXT,
    skipQuery: skipWorklistQueries,
  });
  const { saveLastViewedCustomWorklists } = useSaveLastViewedCustomWorklists({
    context: APOLLO_QUERY_CONTEXT,
  });
  const [getCustomWorklistDetails, { loading: areCustomWorklistFiltersLoading }] =
    useLazyQuery<GetCustomWorklistQuery>(GET_CUSTOM_WORKLIST);

  const hasActiveCustomWorklistID = useMemo(() => {
    return shouldUseCustomWorklistPreferences && activeCustomWorklistIDs.length > 0;
  }, [activeCustomWorklistIDs.length, shouldUseCustomWorklistPreferences]);

  useEffect(() => {
    if (overrideSurface != null) {
      setSurface(overrideSurface);
    }
  }, [overrideSurface]);

  useEffect(() => {
    // Only apply URL sorting if we're not using custom worklist preferences
    const shouldApplyUrlSorting = !(
      shouldUseCustomWorklistPreferences && customWorklistSortingPreferences
    );

    if (shouldApplyUrlSorting) {
      setSortColumns(urlSortColumns);
      setSortOrders(urlSortOrders);
    }
  }, [
    urlSortColumns,
    urlSortOrders,
    customWorklistSortingPreferences,
    shouldUseCustomWorklistPreferences,
  ]);

  // Sync sorting state with custom worklist preferences when they change
  useEffect(() => {
    if (shouldUseCustomWorklistPreferences && customWorklistSortingPreferences) {
      setSortColumns(customWorklistSortingPreferences.sortColumns);
      setSortOrders(customWorklistSortingPreferences.sortOrders);
    }
  }, [customWorklistSortingPreferences, shouldUseCustomWorklistPreferences]);

  // LEGACY: This callback is used for saving sorting preferences to the old system.
  // For custom worklist filter preferences, use updateCustomWorklistFilterSettings from useCustomWorklistPreferenceUpdates hook.
  const maybeSaveSortingPreferencesWithCustomWorklist = useCallback(
    ({ surface: savedSurface, ...rest }: SaveSortingPreferencesArgs) => {
      saveSortingPreferences({
        ...rest,
        surface: savedSurface,
        worklistViewID:
          savedSurface === WorklistSurface.CUSTOM_WORKLIST && activeCustomWorklistIDs.length > 0
            ? activeCustomWorklistIDs
            : [],
      });
    },
    [activeCustomWorklistIDs, saveSortingPreferences]
  );

  const trackDefaultWorklistLoad = useCallback(
    (worklistIDs: string[], appliedFilters: Nullable<FilterStateType>) => {
      try {
        const worklistTypes = new Set<WorklistTypeDetailType>();

        for (const worklistID of worklistIDs) {
          const worklist = allWorklists.get(worklistID);
          if (worklist?.internalSettings?.isCanned) {
            worklistTypes.add(WorklistTypeDetail.Canned);
            continue;
          }

          if (isPublicOrCombinedWorklist([worklistID])) {
            worklistTypes.add(WorklistTypeDetail.Public);
          } else {
            worklistTypes.add(WorklistTypeDetail.Private);
          }
        }

        const types = Array.from(worklistTypes).sort();
        const mode =
          activeCustomWorklistIDs.length > 1 ? WorklistMode.Combined : WorklistMode.Single;
        const worklistNames = getCustomWorklistNames(worklistIDs, allWorklists);

        trackWorklistAction(worklist.usr.loadDefaultWorklist, {
          worklistSmids: worklistIDs,
          worklistNames,
          ...(appliedFilters ? buildFilterDetails(appliedFilters) : []),
          worklistTypes: types,
          worklistMode: mode,
        });
      } catch (error) {
        logger.error(
          '[WorklistFiltersContext] Error tracking loadDefaultWorklist analytic',
          error,
          {
            filters: appliedFilters,
            defaultCustomWorklistIDs,
          }
        );
      }
    },
    [
      activeCustomWorklistIDs.length,
      allWorklists,
      defaultCustomWorklistIDs,
      isPublicOrCombinedWorklist,
    ]
  );

  const fetchDefaultCustomWorklistFilters = async () => {
    if (isRefactorEnabled) return;
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    if (
      defaultCustomWorklistIDs.length === 0 ||
      (activeCustomWorklistFilters != null &&
        activeCustomWorklistIDs.length > 0 &&
        hasSameItems(activeCustomWorklistIDs, defaultCustomWorklistIDs))
    ) {
      return;
    }

    isStillProcessingCustomWorklistFiltersRef.current = true;

    // This should already be set in the parent, but just in case, we set it here as well
    if (activeCustomWorklistIDs.length === 0) {
      setActiveCustomWorklistIDs(defaultCustomWorklistIDs);
    }

    if (defaultCustomWorklistIDs.length > 1) {
      logger.info(
        `[WorklistFiltersContext] Fetching combined custom worklists: ${defaultCustomWorklistIDs}`
      );

      const appliedFilters = await updateCombinedCustomWorklists(defaultCustomWorklistIDs);
      trackDefaultWorklistLoad(defaultCustomWorklistIDs, appliedFilters);

      isStillProcessingCustomWorklistFiltersRef.current = false;
      return;
    }

    const customWorklistID = defaultCustomWorklistIDs[0] ?? null;

    updateSurface(WorklistSurface.CUSTOM_WORKLIST, customWorklistID);

    logger.info(
      `[WorklistFiltersContext] Fetching default custom worklist filters for ${customWorklistID}`,
      {
        defaultCustomWorklistIDs: customWorklistID,
      }
    );

    const { data: customWorklistData } = await getCustomWorklistDetails({
      variables: { smid: customWorklistID ?? '' },
      context: APOLLO_QUERY_CONTEXT,
    });

    const filters = formatCustomWorklistFilters(customWorklistData?.customWorklist?.filters || []);
    setActiveCustomWorklistFilters(filters);

    trackDefaultWorklistLoad(defaultCustomWorklistIDs, filters);

    // If overrideURLSearchParams has more than one entry, it means that
    // the user has saved sorting and filter preferences for this worklist
    // and we should not override with the default custom worklist filters
    if (overrideURLSearchParams == null || overrideURLSearchParams?.size < 1) {
      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: filters,
      }));
    }
    isStillProcessingCustomWorklistFiltersRef.current = false;
  };

  useMount(() => {
    fetchDefaultCustomWorklistFilters();
  });

  const surfacesToDefaultFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(surfaceToFilterConfig).map(([surface, { defaultFilters }]: [any, any]) => [
          surface,
          defaultFilters,
        ])
      ),
    [surfaceToFilterConfig]
  );

  const surfacesToLockedFilters: {
    [key: string]: FilterStateType;
  } = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(surfaceToFilterConfig).map(([surface, { lockedFilters }]: [any, any]) => [
          surface,
          lockedFilters,
        ])
      ),
    [surfaceToFilterConfig]
  );

  const initialFilters: FilterStateType = useMemo(() => {
    if (urlFiltersOnLoad.current == null) {
      return {};
    }
    const urlFiltersToApply = urlFiltersOnLoad.current;
    const surfaceFilters = surfacesToDefaultFilters[surface];
    return parseFiltersFromURLParams(urlFiltersToApply, surfaceFilters);
  }, [surfacesToDefaultFilters, surface]);

  const [filtersBySurface, setFiltersBySurface] = useState<FilterStateBySurface>(() => {
    const initialState = {
      ...DEFAULT_EMPTY_WORKLIST_SURFACE_FILTERS,
      [surface as string]: {
        ...initialFilters,
        ...Object.fromEntries(
          Object.entries(surfacesToDefaultFilters[surface] ?? {})
            .filter(
              ([, filterConfig]: [any, any]) =>
                filterConfig.type === FilterVariant.Toggle && filterConfig.defaultValue === 'true'
            )
            .map(([key, filterConfig]: [any, any]) => [key, filterConfig.defaultValue])
        ),
      },
    };

    if (
      activeCustomWorklistFilters != null &&
      defaultCustomWorklistIDs.length > 0 &&
      activeCustomWorklistIDs.length > 0 &&
      hasSameItems(activeCustomWorklistIDs, defaultCustomWorklistIDs)
    ) {
      initialState[WorklistSurface.CUSTOM_WORKLIST] = activeCustomWorklistFilters;
    }

    return initialState;
  });

  const currentFilterValues = useMemo(() => filtersBySurface[surface], [filtersBySurface, surface]);

  const onSortChange = useCallback(
    (columnIds: string[], orders: Array<SortOrder>) => {
      const mappedColumns: Array<SortColumn> = columnIds.map(
        (id) =>
          SORTABLE_WORKLIST_COLUMN_MAP[
            id as keyof typeof SORTABLE_WORKLIST_COLUMN_MAP
          ] as SortColumn
      );

      let changedColumns: boolean = !equals(mappedColumns, sortColumns);
      let changedOrders: boolean = !equals(orders, sortOrders);

      if (changedColumns) {
        setSortColumns(mappedColumns);
        changedColumns = true;
      }

      if (changedOrders) {
        setSortOrders(orders);
        changedOrders = true;
      }

      if (surface === WorklistSurface.STUDIES_DIALOG) {
        return;
      }

      if (changedColumns || changedOrders) {
        // Save to custom worklist preferences when feature flag is enabled and we're on custom worklist
        if (shouldUseCustomWorklistPreferences && activeCustomWorklistIDs.length > 0) {
          updateCustomWorklistSortingSettings(mappedColumns, orders);
        } else {
          maybeSaveSortingPreferencesWithCustomWorklist({
            surface,
            columns: mappedColumns.join(','),
            orders: orders.join(','),
            filters: currentFilterValues,
          });
        }
      }
    },
    [
      sortColumns,
      sortOrders,
      surface,
      maybeSaveSortingPreferencesWithCustomWorklist,
      currentFilterValues,
      shouldUseCustomWorklistPreferences,
      activeCustomWorklistIDs,
      updateCustomWorklistSortingSettings,
    ]
  );

  const updateFilters = useCallback(
    (newFilters: FilterStateType) => {
      const previousFilters = filtersBySurface[surface];
      const updatedFilters = { ...previousFilters, ...newFilters };

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [surface as string]: updatedFilters,
      }));

      if (shouldUseCustomWorklistPreferences && activeCustomWorklistIDs.length > 0) {
        updateCustomWorklistFilterSettings(updatedFilters);
      } else {
        maybeSaveSortingPreferencesWithCustomWorklist({
          surface,
          columns: sortColumns.join(','),
          orders: sortOrders.join(','),
          filters: updatedFilters,
        });
      }

      logger.info(
        `[WorklistFiltersContext] Filters for ${surface}${activeCustomWorklistIDs.length > 0 ? ' with customWorklistID ' + activeCustomWorklistIDs : ''} have been updated`,
        {
          surface,
          oldFilters: previousFilters,
          filters: updatedFilters,
        }
      );
    },
    [
      filtersBySurface,
      surface,
      shouldUseCustomWorklistPreferences,
      activeCustomWorklistIDs,
      updateCustomWorklistFilterSettings,
      maybeSaveSortingPreferencesWithCustomWorklist,
      sortColumns,
      sortOrders,
    ]
  );

  const [searches, debouncedSearches, setSearches] = useDebouncedState<WorklistSurfaceSearchValues>(
    {},
    SEARCH_DEBOUNCE_MS
  );

  const debouncedSearchForSurface = useMemo(() => {
    if (hasActiveCustomWorklistID) {
      return debouncedSearches[activeCustomWorklistIDs[0]] || '';
    }

    return debouncedSearches[surface] ?? '';
  }, [activeCustomWorklistIDs, debouncedSearches, hasActiveCustomWorklistID, surface]);

  const searchForSurface = useMemo(() => {
    if (hasActiveCustomWorklistID) {
      return searches[activeCustomWorklistIDs[0]] || '';
    }

    return searches[surface] ?? '';
  }, [activeCustomWorklistIDs, hasActiveCustomWorklistID, searches, surface]);

  const setSearchForSurface = useCallback(
    (value: string) => {
      let surfaceToSet: string = surface;

      if (
        shouldUseCustomWorklistPreferences &&
        activeCustomWorklistIDs != null &&
        activeCustomWorklistIDs.length > 0
      ) {
        surfaceToSet = activeCustomWorklistIDs[0] || WorklistSurface.CUSTOM_WORKLIST;
      }

      setSearches((currentSearch) => assoc(surfaceToSet, value, currentSearch));
    },
    [activeCustomWorklistIDs, shouldUseCustomWorklistPreferences, setSearches, surface]
  );

  const setSearchForCustomWorklist = useCallback(
    (customWorklistID: string, value: string) => {
      setSearches((currentSearch) => {
        return { ...currentSearch, [customWorklistID]: value };
      });
    },
    [setSearches]
  );

  const replaceFilters = useCallback(
    (newFilters: FilterStateType) => {
      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [surface as string]: newFilters,
      }));

      if (shouldUseCustomWorklistPreferences && activeCustomWorklistIDs.length > 0) {
        updateCustomWorklistFilterSettings(newFilters);
      } else {
        maybeSaveSortingPreferencesWithCustomWorklist({
          surface,
          columns: sortColumns.join(','),
          orders: sortOrders.join(','),
          filters: newFilters,
        });
      }

      if (hasActiveCustomWorklistID) {
        setSearchForCustomWorklist(activeCustomWorklistIDs[0], '');
      }

      logger.info(
        `[WorklistFiltersContext] Filters for ${surface}${activeCustomWorklistIDs.length > 0 ? ' with customWorklistID ' + activeCustomWorklistIDs : ''} have been replaced`,
        {
          surface,
          oldFilters: filtersBySurface[surface],
          filters: newFilters,
        }
      );
    },
    [
      surface,
      shouldUseCustomWorklistPreferences,
      activeCustomWorklistIDs,
      updateCustomWorklistFilterSettings,
      maybeSaveSortingPreferencesWithCustomWorklist,
      sortColumns,
      sortOrders,
      hasActiveCustomWorklistID,
      filtersBySurface,
      setSearchForCustomWorklist,
    ]
  );

  const resetAllFilters = useCallback(() => {
    logger.info(
      `[WorklistFiltersContext] Resetting all filters for ${surface}${' with customWorklistID ' + activeCustomWorklistIDs}`
    );

    const emptyFilters = surfacesToLockedFilters[surface];
    setFiltersBySurface((prevFilters) => ({
      ...prevFilters,
      [surface as string]: emptyFilters,
    }));
    setSearchForSurface('');

    if (hasActiveCustomWorklistID) {
      setSearchForCustomWorklist(activeCustomWorklistIDs[0], '');
    }

    if (shouldUseCustomWorklistPreferences && activeCustomWorklistIDs.length > 0) {
      updateCustomWorklistFilterSettings(emptyFilters);
    } else {
      maybeSaveSortingPreferencesWithCustomWorklist({
        surface,
        columns: sortColumns.join(','),
        orders: sortOrders.join(','),
        filters: {},
      });
    }
  }, [
    surface,
    activeCustomWorklistIDs,
    surfacesToLockedFilters,
    setSearchForSurface,
    hasActiveCustomWorklistID,
    setSearchForCustomWorklist,
    shouldUseCustomWorklistPreferences,
    updateCustomWorklistFilterSettings,
    maybeSaveSortingPreferencesWithCustomWorklist,
    sortColumns,
    sortOrders,
  ]);

  const resetFilter = useCallback(
    (filterKey: WorklistFiltersType) => {
      const previousFilters = filtersBySurface[surface];
      const { [filterKey]: _removed, ...nextSurfaceFilters } = previousFilters; // immutable "omit"

      logger.info(
        `[WorklistFiltersContext] Resetting ${filterKey} filter for ${surface}${activeCustomWorklistIDs.length > 0 ? ' with customWorklistID ' + activeCustomWorklistIDs : ''}`
      );

      if (shouldUseCustomWorklistPreferences && activeCustomWorklistIDs.length > 0) {
        updateCustomWorklistFilterSettings(nextSurfaceFilters);
      } else {
        maybeSaveSortingPreferencesWithCustomWorklist({
          surface,
          columns: sortColumns.join(','),
          orders: sortOrders.join(','),
          filters: nextSurfaceFilters,
        });
      }

      setFiltersBySurface((prevFilters) => {
        const { [filterKey]: _removed, ...newFilters } = prevFilters[surface];
        return { ...prevFilters, [surface as string]: newFilters };
      });
    },
    [
      filtersBySurface,
      surface,
      activeCustomWorklistIDs,
      shouldUseCustomWorklistPreferences,
      updateCustomWorklistFilterSettings,
      maybeSaveSortingPreferencesWithCustomWorklist,
      sortColumns,
      sortOrders,
    ]
  );

  const loadSavedSortingPreferences = useCallback(
    ({ worklistIDs, surface }: { worklistIDs: string[]; surface: WorklistSurfaceType }): void => {
      // Skip loading saved sorting preferences if we're using custom worklist preferences AND they exist
      if (
        shouldUseCustomWorklistPreferences &&
        worklistIDs.length > 0 &&
        customWorklistSortingPreferences
      ) {
        setSurface(surface);
        return;
      }

      let newSortColumn: Array<SortColumn> = DEFAULT_SORT_COLUMNS;
      let newSortOrder: Array<SortOrder> = DEFAULT_SORT_ORDERS;

      if (
        [WorklistSurface.RECENTLY_READ, WorklistSurface.ARCHIVE].some((comparatorSurface) =>
          isAnyProvidedCannedWorklist({
            currentSurface: surface,
            comparatorSurface,
            customWorklistNames: activeCustomWorklistNames,
          })
        )
      ) {
        newSortColumn = [SortColumn.SubmittedAt];
        newSortOrder = [SortOrder.Desc];
      } else if (sortColumns.includes(SortColumn.SubmittedAt)) {
        newSortColumn = [SortColumn.StudyDatetime];
        newSortOrder = [SortOrder.Asc];
      }

      const savedPreferenceKey =
        worklistIDs.length > 0 ? `${surface}_${getWorklistKey(worklistIDs)}` : surface;

      if (savedSortingPreferences[savedPreferenceKey] != null) {
        const params = new URLSearchParams(savedSortingPreferences[savedPreferenceKey]);
        const column = params.get('sortColumn') ?? '';
        const order = params.get('sortOrder') ?? '';
        newSortColumn = validateSortColumns(column.split(','), newSortColumn);
        newSortOrder = validateSortOrders(order.split(','), newSortOrder);
      }

      setSurface(surface);
      logger.info(
        `[WorklistFiltersContext] Loading saved sorting preferences for ${surface} with worklistIDs: ${worklistIDs}`
      );
      if (newSortColumn != null && newSortOrder != null) {
        setSortColumns(newSortColumn);
        setSortOrders(newSortOrder);
      }
    },
    [
      activeCustomWorklistNames,
      savedSortingPreferences,
      sortColumns,
      shouldUseCustomWorklistPreferences,
      customWorklistSortingPreferences,
    ]
  );

  const updateSurface = useCallback(
    (newSurface: WorklistSurfaceType, customWorklistID?: string) => {
      logger.info(
        `[WorklistFiltersContext] Updating surface from ${surface}${activeCustomWorklistIDs[0] ? ' with customWorklistID ' + activeCustomWorklistIDs[0] : ''} to ${newSurface}${
          customWorklistID ? ' with customWorklistID ' + customWorklistID : ''
        }`
      );

      if (
        newSurface === surface &&
        activeCustomWorklistIDs.length > 0 &&
        activeCustomWorklistIDs[0] === customWorklistID
      ) {
        return;
      }

      loadSavedSortingPreferences({
        worklistIDs: customWorklistID ? [customWorklistID] : [],
        surface: newSurface,
      });

      // This is only here to support the legacy worklists. We will have something
      // separate in the future for custom worklists
      if (
        !(
          [WorklistSurface.PLACEHOLDER, WorklistSurface.CUSTOM_WORKLIST] as WorklistSurfaceType[]
        ).includes(surface)
      ) {
        saveLastViewedCustomWorklists([
          {
            surface: newSurface,
            worklistViewID: [],
          },
        ]);
      }
    },
    [activeCustomWorklistIDs, loadSavedSortingPreferences, saveLastViewedCustomWorklists, surface]
  );

  const updateSurfaceAndCustomWorklist = useCallback(
    async (customWorklistID: string) => {
      if (customWorklistID == null) {
        requestedCustomWorklistIDsRef.current = null;
        return;
      }

      requestedCustomWorklistIDsRef.current = customWorklistID;
      isStillProcessingCustomWorklistFiltersRef.current = true;
      currentCustomWorklistRequest.current = customWorklistID;

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: { [CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY]: ['true'] },
      }));

      updateSurface(WorklistSurface.CUSTOM_WORKLIST, customWorklistID);

      logger.info(
        `[WorklistFiltersContext] Switched to custom worklist ${customWorklistID} from ${activeCustomWorklistIDs.length > 0 ? activeCustomWorklistIDs : 'none'}`
      );

      let filtersToApply = {};

      const { data: customWorklistData } = await getCustomWorklistDetails({
        variables: { smid: customWorklistID },
        context: APOLLO_QUERY_CONTEXT,
      });

      // Ignore if a newer request has started
      if (currentCustomWorklistRequest.current !== customWorklistID) {
        return;
      }

      const customWorklistFilters = formatCustomWorklistFilters(
        customWorklistData?.customWorklist?.filters || []
      );

      if (
        customWorklistFilterPreferences != null &&
        Object.keys(customWorklistFilterPreferences).length > 0
      ) {
        filtersToApply = customWorklistFilterPreferences;
        logger.info(
          `[WorklistFiltersContext] Custom worklist ${customWorklistID} using new filter preferences system`,
          { filters: filtersToApply }
        );
      } else if (isFilterPersistenceDisabled) {
        // Persistence is off: skip legacy URL-derived prefs and use the worklist definition.
        filtersToApply = customWorklistFilters;
        logger.info(
          `[WorklistFiltersContext] Custom worklist ${customWorklistID} persistence disabled, using filters from definition`,
          { filters: filtersToApply }
        );
      } else {
        // Fall back to legacy saved sorting preferences
        const savedPreferences =
          savedSortingPreferences[`${WorklistSurface.CUSTOM_WORKLIST}_${customWorklistID}`];

        if (savedPreferences != null) {
          const { filters } = getWorklistURLParams(new URLSearchParams(savedPreferences));

          if (filters != null) {
            filtersToApply = parseFiltersFromURLParams(
              filters,
              surfacesToDefaultFilters[WorklistSurface.CUSTOM_WORKLIST]
            );
            logger.info(
              `[WorklistFiltersContext] Custom worklist ${customWorklistID} has saved preferences (legacy)`,
              { filters: filtersToApply }
            );
          }
        } else {
          filtersToApply = customWorklistFilters;
          logger.info(
            `[WorklistFiltersContext] Custom worklist ${customWorklistID} has no saved preferences, using filters from definition`,
            { filters: filtersToApply }
          );
        }
      }

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: filtersToApply,
      }));

      setActiveCustomWorklistFilters(customWorklistFilters);
      isStillProcessingCustomWorklistFiltersRef.current = false;
      requestedCustomWorklistIDsRef.current = null;
    },
    [
      activeCustomWorklistIDs,
      getCustomWorklistDetails,
      savedSortingPreferences,
      setActiveCustomWorklistFilters,
      surfacesToDefaultFilters,
      updateSurface,
      customWorklistFilterPreferences,
      isFilterPersistenceDisabled,
    ]
  );

  const updateCombinedCustomWorklists = useCallback(
    async (activeWorklistIds: string[]): Promise<Nullable<FilterStateType>> => {
      const worklistKey = getWorklistKey(activeWorklistIds);
      isStillProcessingCustomWorklistFiltersRef.current = true;
      requestedCustomWorklistIDsRef.current = worklistKey;

      setActiveCustomWorklistFilters({});

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: { [CUSTOM_WORKLIST_FILTERS_IN_FLIGHT_KEY]: ['true'] },
      }));

      loadSavedSortingPreferences({
        worklistIDs: activeWorklistIds,
        surface: WorklistSurface.CUSTOM_WORKLIST,
      });

      let filtersToApply = {};

      if (
        customWorklistFilterPreferences != null &&
        Object.keys(customWorklistFilterPreferences).length > 0
      ) {
        filtersToApply = customWorklistFilterPreferences;
        logger.info(
          `[WorklistFiltersContext] Combined Custom worklist ${worklistKey} using new filter preferences system`,
          { filters: filtersToApply }
        );
      } else if (isFilterPersistenceDisabled) {
        // Persistence is off: skip legacy URL-derived prefs and seed with just the Views filter.
        filtersToApply = {
          [WorklistFilters.Views]: activeWorklistIds,
        };
      } else {
        // Fall back to legacy saved sorting preferences
        const savedPreferenceKey = `${surface}_${worklistKey}`;
        const savedPreferences = savedSortingPreferences[savedPreferenceKey];

        if (savedPreferences != null) {
          const { filters } = getWorklistURLParams(new URLSearchParams(savedPreferences));

          if (filters != null) {
            filtersToApply = parseFiltersFromURLParams(
              filters,
              surfacesToDefaultFilters[WorklistSurface.CUSTOM_WORKLIST]
            );
            logger.info(
              `[WorklistFiltersContext] Combined Custom worklist ${worklistKey} has saved preferences (legacy)`,
              { filters: filtersToApply }
            );
          }
        } else {
          filtersToApply = {
            [WorklistFilters.Views]: activeWorklistIds,
          };
        }
      }

      setFiltersBySurface((prevFilters) => ({
        ...prevFilters,
        [WorklistSurface.CUSTOM_WORKLIST]: {
          ...filtersToApply,
          [WorklistFilters.Views]: activeWorklistIds,
        },
      }));

      isStillProcessingCustomWorklistFiltersRef.current = false;
      requestedCustomWorklistIDsRef.current = null;

      return filtersToApply;
    },
    [
      loadSavedSortingPreferences,
      savedSortingPreferences,
      setActiveCustomWorklistFilters,
      surface,
      surfacesToDefaultFilters,
      customWorklistFilterPreferences,
      isFilterPersistenceDisabled,
    ]
  );

  const updateSurfaceAndSearch = useCallback(
    (newSurface: WorklistSurfaceType, newSearch: string) => {
      logger.info(
        `[WorklistFiltersContext] Updating  surface to ${newSurface} with search "${newSearch}"`
      );

      setSearches((currentSearch) => assoc(newSurface, newSearch, currentSearch));
      updateSurface(newSurface);
    },
    [setSearches, updateSurface]
  );

  const getAppliedFilters = useCallback(() => {
    const filters = { ...filtersBySurface[surface], ...surfacesToLockedFilters[surface] } as const;
    const nonEmptyFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]: [any, any]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return value != null && value !== '';
      })
    );

    return Object.keys(nonEmptyFilters);
  }, [filtersBySurface, surfacesToLockedFilters, surface]);

  const currentFilters = useMemo(
    () => ({ ...filtersBySurface[surface], ...surfacesToLockedFilters[surface] }),
    [filtersBySurface, surface, surfacesToLockedFilters]
  );

  const contextBag = useMemo(
    () => ({
      sortColumns,
      sortOrders,
      filters: currentFilters,
      lockedFilters: surfacesToLockedFilters[surface],
      updateFilters,
      replaceFilters,
      surface,
      updateSurface,
      setSearchForSurface,
      setSearchForCustomWorklist,
      searchForSurface,
      debouncedSearchForSurface,
      onSortChange,
      availableFilters: surfacesToDefaultFilters[surface],
      resetAllFilters,
      resetFilter,
      getAppliedFilters,
      filtersBySurface,
      debouncedSearches,
      surfacesToDefaultFilters,
      updateCombinedCustomWorklists,
      updateSurfaceAndCustomWorklist,
      updateSurfaceAndSearch,
      surfacesToLockedFilters,
      areCustomWorklistFiltersLoading,
      isStillProcessingCustomWorklistFiltersRef,
      requestedCustomWorklistIDsRef,
    }),
    [
      currentFilters,
      debouncedSearchForSurface,
      debouncedSearches,
      filtersBySurface,
      getAppliedFilters,
      onSortChange,
      resetAllFilters,
      resetFilter,
      searchForSurface,
      setSearchForSurface,
      setSearchForCustomWorklist,
      sortColumns,
      sortOrders,
      surface,
      surfacesToDefaultFilters,
      surfacesToLockedFilters,
      updateFilters,
      replaceFilters,
      updateSurface,
      updateCombinedCustomWorklists,
      updateSurfaceAndCustomWorklist,
      updateSurfaceAndSearch,
      areCustomWorklistFiltersLoading,
      requestedCustomWorklistIDsRef,
    ]
  );
  return (
    <WorklistFiltersContext.Provider value={contextBag}>{children}</WorklistFiltersContext.Provider>
  );
};

export const useWorklistFiltersContext = (): WorklistFiltersContextBag => {
  const worklistFilters = useContext(WorklistFiltersContext);

  if (!worklistFilters) {
    throw new Error(
      'useWorklistFiltersContext must be used within a WorklistFiltersContextProvider.'
    );
  }

  return worklistFilters;
};

WorklistFiltersContextProvider.displayName = 'WorklistFiltersContextProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorklistFiltersContextProvider);
